/**
 * Availability vs staffing. Business lead 2026-10-27 (owner decision): MSRs now mark the days they CAN work
 * (msr_available_days, migration 1150); staffing an MSR on a day they did NOT mark available is ALLOWED but warned.
 * Full-time (non-MSR) positions never warn. The names below keep "timeOff" so every caller is unchanged; the data
 * is now "days not marked available" from msr_days_not_available.
 *
 * Previously (MSR QA 10-20): staffing a rep on a day they marked as time off was allowed but warned.
 *
 * - Reps take whole days off only (MSR feedback #3). A block that touches a date (start_date <= date <= end_date) is a
 *   day off -- including older timed blocks from before #3. There is no partial-day / overlap logic on purpose.
 * - Dates compare as the EVENT's local calendar date (its timezone, Central by default): event_days.service_date when
 *   the event has dated days, otherwise the local dates the event's start..end spans.
 * - Read-only: the existing sales_rep_availability read (staff roles read every rep's blocks; an MSR reads their own).
 */
import { supabase } from "./supabase";
import { getDaysNotAvailable } from "./msrFeedback.service";
import { getEventDaysFor } from "./msrPortal.service";

export const DEFAULT_EVENT_TZ = "America/Chicago";

/** The local YYYY-MM-DD of an instant in a timezone. */
export function localDateOf(
  iso: string,
  tz: string = DEFAULT_EVENT_TZ,
): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || DEFAULT_EVENT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Every local date from start to end (inclusive), in the event's timezone. Bounded to 62 days. */
export function localDatesBetween(
  startIso: string,
  endIso: string | null | undefined,
  tz: string = DEFAULT_EVENT_TZ,
): string[] {
  const first = localDateOf(startIso, tz);
  const last = endIso ? localDateOf(endIso, tz) : first;
  const out: string[] = [];
  const d = new Date(`${first}T12:00:00Z`);
  for (let i = 0; i < 62; i++) {
    const s = d.toISOString().slice(0, 10);
    if (s > last) {
      break;
    }
    out.push(s);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out.length ? out : [first];
}

export interface TimeOffBlock {
  sales_position_id: string;
  start_date: string;
  end_date: string;
}

/** The dates (from `dates`) a block covers. */
export function datesCovered(block: TimeOffBlock, dates: string[]): string[] {
  return dates.filter((d) => block.start_date <= d && d <= block.end_date);
}

/** positionId -> sorted local dates (from `dates`) that the rep has time off. Positions with none are absent. */
export function timeOffByPosition(
  blocks: TimeOffBlock[],
  dates: string[],
): Record<string, string[]> {
  const out: Record<string, Set<string>> = {};
  for (const b of blocks) {
    for (const d of datesCovered(b, dates)) {
      (out[b.sales_position_id] ??= new Set()).add(d);
    }
  }
  return Object.fromEntries(
    Object.entries(out).map(([k, v]) => [k, [...v].sort()]),
  );
}

/** "Tue, Oct 20" for a YYYY-MM-DD (a calendar date: no timezone shift). */
export function dayLabel(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function joinDays(dates: string[]): string {
  const labels = dates.map(dayLabel);
  return labels.length <= 2
    ? labels.join(" and ")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** "MSR Test hasn't marked Tue, Oct 20 as available." */
export function timeOffSentence(name: string, dates: string[]): string {
  return `${name || "This rep"} hasn't marked ${joinDays(dates)} as available.`;
}

/** The event's local dates: its dated days if it has them, else the local dates its start..end spans. */
export async function getEventLocalDates(eventId: string): Promise<string[]> {
  const days = await getEventDaysFor([eventId]);
  if (days.length) {
    return [...new Set(days.map((d) => d.service_date))].sort();
  }
  const { data, error } = await supabase
    .from("events")
    .select("start_time, end_time, timezone")
    .eq("id", eventId)
    .maybeSingle();
  if (error || !data?.start_time) {
    return [];
  }
  return localDatesBetween(
    data.start_time,
    data.end_time,
    data.timezone ?? DEFAULT_EVENT_TZ,
  );
}

/** Days (from `dates`) each MSR position has NOT marked available (read-only). On a read failure returns {} -- a
 *  warning, never a block. */
export async function getTimeOffConflicts(
  positionIds: string[],
  dates: string[],
): Promise<Record<string, string[]>> {
  const ids = [...new Set(positionIds.filter(Boolean))];
  if (!ids.length || !dates.length) {
    return {};
  }
  try {
    return await getDaysNotAvailable(ids, [...dates].sort());
  } catch (e) {
    console.warn("availability check skipped", e);
    return {};
  }
}

/** Every calendar date from start to end inclusive (YYYY-MM-DD strings; bounded to 62 days). */
export function datesInclusive(start: string, end?: string | null): string[] {
  if (!start) {
    return [];
  }
  const last = end && end >= start ? end : start;
  const out: string[] = [];
  const d = new Date(`${start}T12:00:00Z`);
  for (let i = 0; i < 62; i++) {
    const s = d.toISOString().slice(0, 10);
    if (s > last) {
      break;
    }
    out.push(s);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}
